import { DateTime } from 'luxon';
import { StatementRelativePeriods } from './contact';
import { StatusEnum } from './xero';
/** If policy is active, or requires approval */
export var PolicyState;
(function (PolicyState) {
    PolicyState["ACTIVE"] = "active";
    PolicyState["INACTIVE"] = "inactive";
    PolicyState["APPROVAL"] = "approval";
})(PolicyState || (PolicyState = {}));
export var PolicyType;
(function (PolicyType) {
    PolicyType["REMINDER"] = "reminder";
    PolicyType["LATE_FEE"] = "fee";
    PolicyType["DISCOUNT"] = "discount";
    PolicyType["SMS"] = "sms";
    /** E.g issued once per month per contact */
    PolicyType["STATEMENT_LATE_FEE"] = "statement_late_fee";
    PolicyType["STATEMENT"] = "statement";
    PolicyType["ESCALATION"] = "escalation";
    PolicyType["CONTACT_ESCALATION"] = "contact_escalation";
    PolicyType["QUOTE_EMAIL"] = "quote_email";
    /** Payment Plans */
    PolicyType["PAYMENT_PLAN_REMINDER"] = "payment_plan_reminder";
    /** Hidden placeholder for on-demand/manual actions */
    PolicyType["ON_DEMAND_PLACEHOLDER"] = "on_demand_placeholder";
})(PolicyType || (PolicyType = {}));
export var PolicyTargetStatus;
(function (PolicyTargetStatus) {
    PolicyTargetStatus["DUE"] = "due";
    PolicyTargetStatus["OLDEST_DUE"] = "oldest_due";
    /** @todo */
    PolicyTargetStatus["MARKED_PAID"] = "marked_paid";
    /** The invoice issue date */
    PolicyTargetStatus["ISSUED"] = "issued";
    /** The expiry date when using quotes */
    PolicyTargetStatus["EXPIRY_DATE"] = "expiry_date";
    /** The issue date when using quotes */
    PolicyTargetStatus["QUOTE_ISSUE_DATE"] = "quote_issue_date";
    /** The date of oldest unpaid instalment if the invoice has a payment plan */
    PolicyTargetStatus["INSTALMENT_DUE_DATE"] = "instalment_due_date";
    /** The date the payment plan was created */
    PolicyTargetStatus["PAYMENT_PLAN_CREATED"] = "payment_plan_created";
    /** Invoice amount has changed */
    PolicyTargetStatus["INVOICE_AMOUNT_UPDATED"] = "invoice_amount_updated";
    /** A payment promise was broken (see payment_promise) */
    PolicyTargetStatus["PROMISE_BROKEN"] = "promise_broken";
    /** The promised payment date of the open payment promise */
    PolicyTargetStatus["PROMISE_DUE"] = "promise_due";
})(PolicyTargetStatus || (PolicyTargetStatus = {}));
export var PolicyCalculationType;
(function (PolicyCalculationType) {
    PolicyCalculationType["FIXED"] = "fixed";
    PolicyCalculationType["PERCENT"] = "percent";
    PolicyCalculationType["PRORATA"] = "prorata";
    PolicyCalculationType["NOT_APPLICABLE"] = "not_applicable";
})(PolicyCalculationType || (PolicyCalculationType = {}));
export var PolicyProcessType;
(function (PolicyProcessType) {
    /** Add late fee as new invoice */
    PolicyProcessType["NEW_INVOICE"] = "new_invoice";
    /** Add late fee as a line item on existing */
    PolicyProcessType["UPDATE_INVOICE"] = "update_invoice";
})(PolicyProcessType || (PolicyProcessType = {}));
export var PolicyEscalationType;
(function (PolicyEscalationType) {
    PolicyEscalationType["CALL"] = "call";
    PolicyEscalationType["STOP_CREDIT"] = "stop_credit";
    PolicyEscalationType["LETTER"] = "letter";
    PolicyEscalationType["OTHER"] = "other";
})(PolicyEscalationType || (PolicyEscalationType = {}));
export var PolicyIntervalType;
(function (PolicyIntervalType) {
    PolicyIntervalType["MONTHLY"] = "monthly";
    PolicyIntervalType["WEEKLY"] = "weekly";
    PolicyIntervalType["MONTHLY_WEEKDAYS"] = "monthly_weekdays";
})(PolicyIntervalType || (PolicyIntervalType = {}));
export var PolicyAmountsTaxLineType;
(function (PolicyAmountsTaxLineType) {
    PolicyAmountsTaxLineType["INCLUSIVE"] = "inclusive";
    PolicyAmountsTaxLineType["EXCLUSIVE"] = "exclusive";
    PolicyAmountsTaxLineType["NO_TAX"] = "no_tax";
    PolicyAmountsTaxLineType["NOT_APPLICABLE"] = "not_applicable";
})(PolicyAmountsTaxLineType || (PolicyAmountsTaxLineType = {}));
export var PolicyStatementType;
(function (PolicyStatementType) {
    PolicyStatementType["OUTSTANDING"] = "outstanding";
    PolicyStatementType["OVERDUE"] = "overdue";
    PolicyStatementType["ALL"] = "all";
    PolicyStatementType["IN_CREDIT"] = "in_credit";
})(PolicyStatementType || (PolicyStatementType = {}));
export var PolicyDayPeriodType;
(function (PolicyDayPeriodType) {
    PolicyDayPeriodType["DAYS"] = "day";
    PolicyDayPeriodType["WEEKDAYS"] = "weekday";
    PolicyDayPeriodType["DAYS_OR_LATER"] = "days_or_later";
    PolicyDayPeriodType["WEEKDAYS_OR_LATER"] = "weekdays_or_later";
})(PolicyDayPeriodType || (PolicyDayPeriodType = {}));
export var PolicyStatementInterestTo;
(function (PolicyStatementInterestTo) {
    /** When the actions runs */
    PolicyStatementInterestTo["DEFAULT"] = "default";
    /** The end of the previous month */
    PolicyStatementInterestTo["END_OF_PREVIOUS_MONTH"] = "end_of_previous_month";
})(PolicyStatementInterestTo || (PolicyStatementInterestTo = {}));
export var PolicyIncludeExcludeType;
(function (PolicyIncludeExcludeType) {
    PolicyIncludeExcludeType["INCLUDE"] = "include";
    PolicyIncludeExcludeType["EXCLUDE"] = "exclude";
})(PolicyIncludeExcludeType || (PolicyIncludeExcludeType = {}));
export var PolicyContactAddressField;
(function (PolicyContactAddressField) {
    PolicyContactAddressField["COUNTRY"] = "country";
    PolicyContactAddressField["STATE"] = "state";
    PolicyContactAddressField["CITY"] = "city";
})(PolicyContactAddressField || (PolicyContactAddressField = {}));
export var PolicyContactAddressOperator;
(function (PolicyContactAddressOperator) {
    PolicyContactAddressOperator["IS"] = "is";
    PolicyContactAddressOperator["IS_NOT"] = "is_not";
    PolicyContactAddressOperator["CONTAINS"] = "contains";
})(PolicyContactAddressOperator || (PolicyContactAddressOperator = {}));
export const policyWeeklyDayIntervalMap = {
    1: `Monday`,
    2: `Tuesday`,
    3: `Wednesday`,
    4: `Thursday`,
    5: `Friday`,
    6: `Saturday`,
    7: `Sunday`,
};
/** Title every policy type is created with. Used to detect policies the user never renamed. */
export const DEFAULT_POLICY_TITLES = {
    [PolicyType.LATE_FEE]: 'New Invoice Late Fee',
    [PolicyType.STATEMENT_LATE_FEE]: 'New Statement Interest',
    [PolicyType.DISCOUNT]: 'New Discount',
    [PolicyType.REMINDER]: 'New Email',
    [PolicyType.ESCALATION]: 'New Escalation',
    [PolicyType.CONTACT_ESCALATION]: 'New Contact Escalation',
    [PolicyType.SMS]: 'New SMS Alert',
    [PolicyType.STATEMENT]: 'New Auto Statement',
    [PolicyType.QUOTE_EMAIL]: 'New Quote Email',
    [PolicyType.PAYMENT_PLAN_REMINDER]: 'New Payment Plan Reminder',
    [PolicyType.ON_DEMAND_PLACEHOLDER]: 'On Demand Placeholder',
};
/** True when the title is still a default one, e.g. `New Email` or the de-duplicated `New Email (2)` */
export function isDefaultPolicyTitle(title) {
    if (!title) {
        return false;
    }
    const base = title.trim().replace(/ \(\d+\)$/, '');
    return base === 'New Policy' || Object.values(DEFAULT_POLICY_TITLES).includes(base);
}
export class Policy {
    constructor(options) {
        var _a, _b, _c;
        this.organisation_id = options.organisation_id;
        this.title = options.title || 'New Policy';
        this.deleted = false;
        this.policy_type = options.policy_type || PolicyType.LATE_FEE;
        this.minimum_threshold = options.minimum_threshold || 0;
        this.maximum_threshold = options.maximum_threshold || 0;
        this.statement_period = options.statement_period || StatementRelativePeriods.ALL_TIME;
        this.hour_of_day = options.hour_of_day || null;
        this.system = options.system || false;
        this.contact_tags = options.contact_tags || null;
        this.contact_tags_exclude_include = options.contact_tags_exclude_include || PolicyIncludeExcludeType.INCLUDE;
        this.contact_address_filter_field = options.contact_address_filter_field || null;
        this.contact_address_filter_operator = options.contact_address_filter_operator || null;
        this.contact_address_filter_value = options.contact_address_filter_value || null;
        this.xero_group_name = options.xero_group_name || null;
        this.xero_group_name_exclude_include = options.xero_group_name_exclude_include || PolicyIncludeExcludeType.INCLUDE;
        this.state = options.state || PolicyState.ACTIVE;
        this.day_period_type = options.day_period_type || PolicyDayPeriodType.DAYS;
        this.email_sender_profile_id = options.email_sender_profile_id || null;
        this.webhook_disabled = (_a = options.webhook_disabled) !== null && _a !== void 0 ? _a : null;
        this.skip_when_payment_promised = (_b = options.skip_when_payment_promised) !== null && _b !== void 0 ? _b : true;
        this.skip_when_query_open = (_c = options.skip_when_query_open) !== null && _c !== void 0 ? _c : true;
    }
}
export class LateFeePolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d, _e;
        options.policy_type = PolicyType.LATE_FEE;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.LATE_FEE];
        super(options);
        this.id = options.id;
        this.calculation_type = options.calculation_type || PolicyCalculationType.FIXED;
        this.process_type = options.process_type || PolicyProcessType.NEW_INVOICE;
        this.fixed_fee_amount = options.fixed_fee_amount || 1000;
        this.percent = options.percent || 5.0;
        this.grace_period = options.grace_period || 30;
        this.invoice_status = options.invoice_status || StatusEnum.AUTHORISED;
        this.repeating = options.repeating || false;
        this.repeat_frequency = options.repeat_frequency || 30;
        this.use_contact_tax_rate = options.use_contact_tax_rate || false;
        this.line_item_text = options.line_item_text || 'Late Fee Issued On #PenaltyDate';
        this.apply_after_due_date_string = options.apply_after_due_date_string || DateTime.now().minus({ months: 3 }).toISODate();
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.xero_account_code = options.xero_account_code || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.on_update_fail_send_new = (_a = options.on_update_fail_send_new) !== null && _a !== void 0 ? _a : true;
        this.penalty_due_date = (_b = options.penalty_due_date) !== null && _b !== void 0 ? _b : 0;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.use_central_interest_rate = (_c = options.use_central_interest_rate) !== null && _c !== void 0 ? _c : false;
        this.invoice_reference = (_d = options.invoice_reference) !== null && _d !== void 0 ? _d : `Penalty for {{{OriginalInvoiceNumber}}}`;
        this.use_invoice_number_prefix = (_e = options.use_invoice_number_prefix) !== null && _e !== void 0 ? _e : false;
        this.new_invoice_branding_theme_id = options.new_invoice_branding_theme_id || null;
        this.tracking_category_class = options.tracking_category_class || null;
        this.do_not_send_email_to_customer = options.do_not_send_email_to_customer || false;
        this.first_time_only = options.first_time_only || false;
        this.accounting_system_location = options.accounting_system_location || null;
        if (options.target_status === PolicyTargetStatus.DUE || options.target_status === PolicyTargetStatus.ISSUED) {
            this.target_status = options.target_status;
        }
    }
}
export class IntervalLateFeePolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        options.policy_type = PolicyType.STATEMENT_LATE_FEE;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.STATEMENT_LATE_FEE];
        super(options);
        this.calculation_type = options.calculation_type === PolicyCalculationType.PRORATA ? PolicyCalculationType.PRORATA : PolicyCalculationType.PERCENT;
        this.process_type = PolicyProcessType.NEW_INVOICE;
        this.fixed_fee_amount = options.fixed_fee_amount || 0;
        this.percent = options.percent || 10.0;
        this.interval_type = options.interval_type || PolicyIntervalType.MONTHLY;
        this.day_index = options.day_index || 1;
        this.overdue_age_min = (_a = options.overdue_age_min) !== null && _a !== void 0 ? _a : 7;
        this.overdue_age_max = (_b = options.overdue_age_max) !== null && _b !== void 0 ? _b : null;
        this.amounts_tax_line_type = options.amounts_tax_line_type || PolicyAmountsTaxLineType.INCLUSIVE;
        this.invoice_status = options.invoice_status || StatusEnum.AUTHORISED;
        this.adjust_for_credit = (_c = options.adjust_for_credit) !== null && _c !== void 0 ? _c : true;
        this.statement_type = options.statement_type || PolicyStatementType.OVERDUE;
        this.penalty_due_date = (_d = options.penalty_due_date) !== null && _d !== void 0 ? _d : 0;
        this.template_id = options.template_id || null;
        this.min_fee_amount = (_e = options.min_fee_amount) !== null && _e !== void 0 ? _e : null;
        this.xero_account_code = options.xero_account_code || null;
        this.use_central_interest_rate = (_f = options.use_central_interest_rate) !== null && _f !== void 0 ? _f : false;
        this.use_invoice_number_prefix = (_g = options.use_invoice_number_prefix) !== null && _g !== void 0 ? _g : false;
        this.reference_contains = options.reference_contains || null;
        this.include_previous_interest = (_h = options.include_previous_interest) !== null && _h !== void 0 ? _h : true;
        this.new_invoice_branding_theme_id = options.new_invoice_branding_theme_id || null;
        this.statement_interest_to = options.statement_interest_to || PolicyStatementInterestTo.DEFAULT;
        this.parent_only = (_j = options.parent_only) !== null && _j !== void 0 ? _j : false;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.tracking_category_class = options.tracking_category_class || null;
        this.accounting_system_location = options.accounting_system_location || null;
        this.invoice_reference = (_k = options.invoice_reference) !== null && _k !== void 0 ? _k : `Overdue account charges as on {{{IssueDate}}}`;
        this.earliest_interest_date_string = options.earliest_interest_date_string || null;
        this.minimum_threshold = options.minimum_threshold || 0;
        this.fixed_amount_line_description = options.fixed_amount_line_description || null;
        this.fixed_amount_account_code = options.fixed_amount_account_code || null;
        this.fixed_amount_tax_rate = options.fixed_amount_tax_rate || null;
    }
}
export class DiscountPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.DISCOUNT;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.DISCOUNT];
        super(options);
        this.calculation_type = options.calculation_type || PolicyCalculationType.FIXED;
        this.fixed_fee_amount = options.fixed_fee_amount || 1000;
        this.percent = options.percent || 5.0;
        this.grace_period = options.grace_period || 30;
        this.use_contact_tax_rate = options.use_contact_tax_rate || false;
        this.line_item_text = options.line_item_text || `Prompt payment discount if paid by #DiscountDate`;
        this.apply_after_due_date_string = options.apply_after_due_date_string || DateTime.now().minus({ months: 3 }).toISODate();
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.discount_applies = options.discount_applies || `new`;
        this.template_id = options.template_id || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.xero_account_code = options.xero_account_code || null;
        this.target_status = options.target_status || PolicyTargetStatus.ISSUED;
        this.discount_removal_target_status = options.discount_removal_target_status || PolicyTargetStatus.DUE;
        this.day_index = options.day_index || 2;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.mark_as_sent = options.mark_as_sent || null;
    }
}
/** Prefilled prompt when a reminder switches to the Intelligent Dynamic template */
export const DEFAULT_INTELLIGENT_EMAIL_PROMPT = `Write a short, polite payment reminder for this invoice.

Match the tone to the situation. Keep it light if the invoice is not due yet or only a few days late. Be more direct when it is well overdue or we have already sent a few reminders, but stay courteous.

- If they paid us recently, thank them for it.
- If they have other overdue invoices, mention them briefly and point them to their statement.
- If a late fee will be added when the invoice stays unpaid, say how much.
- If they can set up a payment plan, offer it as an option.
- If they have promised to pay, acknowledge the promise instead of chasing.

Keep it under 150 words and always include the link to view and pay the invoice.`;
export class ReminderPolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d;
        options.policy_type = PolicyType.REMINDER;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.REMINDER];
        super(options);
        this.reminder_platform = options.reminder_platform || `email`; // TODO moved to shared for ReminderPlatformEnum.EMAIL,
        this.target_status = options.target_status || PolicyTargetStatus.DUE;
        this.day_index = (_a = options.day_index) !== null && _a !== void 0 ? _a : -3;
        this.include_invoice_pdf = (_b = options.include_invoice_pdf) !== null && _b !== void 0 ? _b : true;
        this.template_id = options.template_id || null;
        this.attach_ics = (_c = options.attach_ics) !== null && _c !== void 0 ? _c : false;
        this.include_attachments = options.include_attachments || false;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.mark_as_sent = options.mark_as_sent || null;
        this.only_send_to_primary_contact = (_d = options.only_send_to_primary_contact) !== null && _d !== void 0 ? _d : null;
        this.destination_email_override = options.destination_email_override || null;
        this.ai_prompt = options.ai_prompt || null;
    }
}
export class EscalationPolicy extends Policy {
    constructor(options) {
        var _a;
        options.policy_type = PolicyType.ESCALATION;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.ESCALATION];
        super(options);
        this.id = options.id;
        this.apply_after_due_date_string = options.apply_after_due_date_string || DateTime.now().minus({ months: 1 }).toISODate();
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.escalation_type = options.escalation_type || PolicyEscalationType.CALL;
        this.escalation_steps = options.escalation_steps || '';
        this.escalation_emails = options.escalation_emails;
        this.day_index = options.day_index || 30;
        this.target_status = options.target_status || PolicyTargetStatus.DUE;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.assignee_user_id = options.assignee_user_id || null;
        this.assign_to_record_assignee = (_a = options.assign_to_record_assignee) !== null && _a !== void 0 ? _a : false;
    }
}
export class EscalationContactPolicy extends Policy {
    constructor(options) {
        var _a;
        options.policy_type = PolicyType.CONTACT_ESCALATION;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.CONTACT_ESCALATION];
        super(options);
        this.id = options.id;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.escalation_type = options.escalation_type || PolicyEscalationType.CALL;
        this.escalation_steps = options.escalation_steps || '';
        this.escalation_emails = options.escalation_emails;
        this.day_index = options.day_index || 30;
        this.target_status = PolicyTargetStatus.OLDEST_DUE;
        this.assignee_user_id = options.assignee_user_id || null;
        this.assign_to_record_assignee = (_a = options.assign_to_record_assignee) !== null && _a !== void 0 ? _a : false;
        this.apply_after_due_date_string = options.apply_after_due_date_string || null;
    }
}
const defaultMessageText = `
  Hi {{{InvoiceContactName}}},
  Just a friendly reminder that your invoice {{{InvoiceNumber}}} from {{{OrganisationName}}} was due on {{{DueDate}}}.\n
`;
export class SmsPolicy extends Policy {
    constructor(options) {
        var _a, _b;
        options.policy_type = PolicyType.SMS;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.SMS];
        super(options);
        this.sms_content = options.sms_content || defaultMessageText;
        this.target_status = options.target_status || PolicyTargetStatus.DUE;
        this.day_index = options.day_index || 3;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.only_webhook = (_a = options.only_webhook) !== null && _a !== void 0 ? _a : false;
        this.sms_fallback_to_phone = (_b = options.sms_fallback_to_phone) !== null && _b !== void 0 ? _b : false;
        this.sms_default_country_code = options.sms_default_country_code || null;
        this.exclude_account_lines = options.exclude_account_lines || null;
    }
}
export class StatementPolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f;
        options.policy_type = PolicyType.STATEMENT;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.STATEMENT];
        super(options);
        this.template_id = options.template_id || null;
        this.interval_type = options.interval_type || PolicyIntervalType.MONTHLY;
        this.day_index = options.day_index || 1;
        this.statement_type = options.statement_type || PolicyStatementType.OUTSTANDING;
        this.minimum_threshold = options.minimum_threshold || 0;
        this.overdue_age_min = (_a = options.overdue_age_min) !== null && _a !== void 0 ? _a : 0;
        this.destination_email_override = options.destination_email_override || null;
        this.parent_only = (_b = options.parent_only) !== null && _b !== void 0 ? _b : false;
        this.include_statement_pdf = (_c = options.include_statement_pdf) !== null && _c !== void 0 ? _c : true;
        this.include_activity_pdf = (_d = options.include_activity_pdf) !== null && _d !== void 0 ? _d : false;
        this.statement_template_id = (_e = options.statement_template_id) !== null && _e !== void 0 ? _e : null;
        this.use_standard_statement = (_f = options.use_standard_statement) !== null && _f !== void 0 ? _f : false;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
    }
}
export class QuotePolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.QUOTE_EMAIL;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.QUOTE_EMAIL];
        super(options);
        this.day_index = options.day_index || -3;
        this.target_status = options.target_status || PolicyTargetStatus.EXPIRY_DATE;
        this.hour_of_day = options.hour_of_day || 12;
        this.destination_email_override = options.destination_email_override || null;
        this.template_id = options.template_id || null;
    }
}
export class PaymentPlanReminderPolicy extends Policy {
    constructor(options) {
        var _a, _b;
        options.policy_type = PolicyType.PAYMENT_PLAN_REMINDER;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.PAYMENT_PLAN_REMINDER];
        super(options);
        this.day_index = (_a = options.day_index) !== null && _a !== void 0 ? _a : -3;
        this.target_status = options.target_status || PolicyTargetStatus.INSTALMENT_DUE_DATE;
        this.hour_of_day = (_b = options.hour_of_day) !== null && _b !== void 0 ? _b : 12;
        this.template_id = options.template_id || null;
    }
}
export class OnDemandPlaceholderPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.ON_DEMAND_PLACEHOLDER;
        options.title = options.title || DEFAULT_POLICY_TITLES[PolicyType.ON_DEMAND_PLACEHOLDER];
        super(options);
    }
}
export const policyConfig = {
    apply_after_due_date_string: {
        sortIndex: 1,
        label: `Apply only to invoices due from`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION],
        dataType: `date`,
    },
    apply_before_due_date_string: {
        sortIndex: 2,
        label: `Apply only to invoices due before`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION],
        dataType: `date`,
    },
    minimum_threshold: {
        sortIndex: 3,
        label: `Minimum Due Amount`,
        advanced: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION, PolicyType.STATEMENT_LATE_FEE],
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.STATEMENT, PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION, PolicyType.STATEMENT_LATE_FEE],
        dataType: `number`,
    },
    maximum_threshold: {
        sortIndex: 4,
        label: `Maximum Due Amount`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.CONTACT_ESCALATION],
        dataType: `number`,
    },
    exclude_branding_themes: {
        sortIndex: 5,
        label: `Exclude Branding Themes`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION, PolicyType.STATEMENT],
        notApplicableAccountingSystems: ['QBO'], // see below: getAccountingSystemsConfigured(ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING)
    },
    use_contact_tax_rate: {
        sortIndex: 6,
        label: `Use Contacts Tax Rate`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT],
        // why does this not work...?
        // notApplicableAccountingSystems: getAccountingSystemsConfigured(ACCOUNTING_CONFIG.HIDE_CONTACT_GROUPS),
        notApplicableAccountingSystems: ['QBO'],
    },
    penalty_due_date: {
        sortIndex: 7,
        label: `Penalty Due Date`,
        advanced: false,
        appliesTo: [PolicyType.LATE_FEE],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    min_fee_amount: {
        sortIndex: 8,
        label: `Minimum Fee Amount`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT_LATE_FEE],
        dataType: `number`,
    },
    exclude_account_lines: {
        sortIndex: 10,
        label: `Exclude/include invoice lines`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.SMS],
    },
    fixed_fee_amount: {
        sortIndex: 11,
        label: `Fixed Amount`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT_LATE_FEE],
    },
    invoice_reference: {
        sortIndex: 12,
        label: `Invoice Reference`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
            policy_type: PolicyType.STATEMENT_LATE_FEE,
        },
    },
    hour_of_day: {
        sortIndex: 13,
        label: `Action Time`,
        appliesTo: [
            PolicyType.STATEMENT_LATE_FEE,
            PolicyType.STATEMENT,
            PolicyType.REMINDER,
            PolicyType.SMS,
            PolicyType.ESCALATION,
            PolicyType.LATE_FEE,
            PolicyType.CONTACT_ESCALATION,
            // PolicyType.DISCOUNT,
        ],
        advanced: [
            PolicyType.STATEMENT_LATE_FEE,
            PolicyType.STATEMENT,
            PolicyType.REMINDER,
            PolicyType.SMS,
            PolicyType.ESCALATION,
            PolicyType.LATE_FEE,
            PolicyType.CONTACT_ESCALATION,
        ],
        doesNotApplySystem: true,
    },
    use_invoice_number_prefix: {
        sortIndex: 14,
        label: `Use custom invoice numbers`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: [`XERO`],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    reference_contains: {
        sortIndex: 15,
        label: `Include Invoices with Reference`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.SMS, PolicyType.DISCOUNT],
    },
    only_webhook: {
        sortIndex: 16,
        label: `Only trigger webhook`,
        advanced: true,
        appliesTo: [PolicyType.SMS],
        requiresFeature: `webhooks`,
    },
    destination_email_override: {
        sortIndex: 17,
        label: `Override Destination Email`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT, PolicyType.REMINDER, PolicyType.QUOTE_EMAIL],
    },
    new_invoice_branding_theme_id: {
        sortIndex: 18,
        label: `Use Branding Theme`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: ['QBO'],
    },
    parent_only: {
        sortIndex: 19,
        label: `Only apply to parent customers`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE],
    },
    sms_default_country_code: {
        sortIndex: 20,
        label: `Default country code`,
        advanced: [PolicyType.SMS],
        appliesTo: [PolicyType.SMS],
    },
    sms_fallback_to_phone: {
        sortIndex: 21,
        label: `Use business phone number`,
        advanced: true,
        appliesTo: [PolicyType.SMS],
    },
    tracking_category_class: {
        sortIndex: 22,
        label: `Use Tracking Category or Class`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
    },
    contact_tags: {
        sortIndex: 22,
        label: `Contact Tags`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.SMS, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE, PolicyType.CONTACT_ESCALATION, PolicyType.QUOTE_EMAIL],
        doesNotApplySystem: true,
    },
    contact_address_filter_field: {
        sortIndex: 23,
        label: `Contact Address`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.SMS, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE, PolicyType.CONTACT_ESCALATION, PolicyType.QUOTE_EMAIL],
        doesNotApplySystem: true,
    },
    xero_group_name: {
        sortIndex: 24,
        label: `Xero Group Name`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.SMS, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE, PolicyType.CONTACT_ESCALATION],
        notApplicableAccountingSystems: ['QBO'],
    },
    do_not_send_email_to_customer: {
        sortIndex: 25,
        label: `Do not send email to customer`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
    },
    first_time_only: {
        sortIndex: 26,
        label: `First time only`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
    },
    /** When creating an escalation, the default assignee user */
    assignee_user_id: {
        sortIndex: 27,
        label: `Default Assignee`,
        advanced: false,
        appliesTo: [PolicyType.ESCALATION],
    },
    mark_as_sent: {
        sortIndex: 28,
        label: `Mark as sent in accounting system`,
        advanced: true,
        appliesTo: [PolicyType.REMINDER, PolicyType.DISCOUNT],
    },
    accounting_system_location: {
        sortIndex: 29,
        label: `Accounting System Location`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: ['XERO'],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    earliest_interest_date_string: {
        sortIndex: 31,
        label: `Earliest Interest Date`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        // only applies to prorata calculations
        requiresAny: {
            calculation_type: PolicyCalculationType.PRORATA,
        },
        dataType: `date`,
    },
    only_send_to_primary_contact: {
        sortIndex: 32,
        label: `Only send to primary contact`,
        advanced: true,
        notApplicableAccountingSystems: ['QBO'],
        appliesTo: [PolicyType.REMINDER, PolicyType.STATEMENT],
    },
    webhook_disabled: {
        sortIndex: 33,
        label: `Disable Webhooks`,
        advanced: true,
        requiresFeature: `webhooks`,
        appliesTo: [
            PolicyType.STATEMENT_LATE_FEE,
            PolicyType.STATEMENT,
            PolicyType.REMINDER,
            PolicyType.SMS,
            PolicyType.ESCALATION,
            PolicyType.LATE_FEE,
            PolicyType.CONTACT_ESCALATION,
            PolicyType.DISCOUNT,
        ],
    },
    target_status: {
        sortIndex: 34,
        label: `Use issue date for grace period`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
    },
};
export const statementTypes = [
    PolicyType.STATEMENT_LATE_FEE,
    PolicyType.STATEMENT,
];
export const contactTypes = [
    ...statementTypes,
    PolicyType.CONTACT_ESCALATION,
];
export const quoteTypes = [
    PolicyType.QUOTE_EMAIL,
];
export const invoiceTypes = [
    PolicyType.LATE_FEE,
    PolicyType.DISCOUNT,
    PolicyType.REMINDER,
    PolicyType.SMS,
    PolicyType.ESCALATION,
];
/** Policies that require a contact email or conditionally require email */
export const policiesNeedingEmail = [
    PolicyType.REMINDER,
    PolicyType.DISCOUNT,
    PolicyType.LATE_FEE,
    PolicyType.PAYMENT_PLAN_REMINDER,
    PolicyType.QUOTE_EMAIL,
    PolicyType.STATEMENT,
    PolicyType.STATEMENT_LATE_FEE,
];
export var PolicyResourceType;
(function (PolicyResourceType) {
    PolicyResourceType["INVOICE"] = "invoice";
    PolicyResourceType["CONTACT"] = "contact";
    PolicyResourceType["QUOTE"] = "quote";
    PolicyResourceType["NOT_APPLICABLE"] = "not_applicable";
})(PolicyResourceType || (PolicyResourceType = {}));
// @ash - a different approach to the above, the above starts at the field level, this starts at the policy level
export const policyConfigV2 = {
    [PolicyType.LATE_FEE]: {
        resourceType: PolicyResourceType.INVOICE,
        requiresSystem: true,
        allowedSystemEditor: true,
        previewConfig: {
            canPreview: true,
            skipFields: [
                `maximum_threshold`,
                `minimum_threshold`,
                `apply_after_due_date_string`,
                `apply_before_due_date_string`,
                `grace_period`,
            ],
        },
    },
    [PolicyType.DISCOUNT]: {
        resourceType: PolicyResourceType.INVOICE,
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.REMINDER]: {
        resourceType: PolicyResourceType.INVOICE,
        previewConfig: {
            canPreview: true,
            skipFields: [],
            mustApplyToPreview: true,
        },
        requiresSystem: true,
        allowedSystemEditor: true,
    },
    [PolicyType.SMS]: {
        resourceType: PolicyResourceType.INVOICE,
        requiresSystem: true,
        allowedSystemEditor: true,
        previewConfig: {
            mustApplyToPreview: true,
            canPreview: true,
            skipFields: [],
        },
    },
    [PolicyType.STATEMENT_LATE_FEE]: {
        resourceType: PolicyResourceType.CONTACT,
        previewConfig: {
            canPreview: true,
            skipFields: [],
        },
        allowedSystemEditor: true,
        requiresSystem: true,
    },
    [PolicyType.STATEMENT]: {
        resourceType: PolicyResourceType.CONTACT,
        previewConfig: {
            canPreview: true,
            skipFields: [],
        },
        allowedSystemEditor: true,
        requiresSystem: true,
    },
    [PolicyType.ESCALATION]: {
        resourceType: PolicyResourceType.INVOICE,
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.CONTACT_ESCALATION]: {
        resourceType: PolicyResourceType.CONTACT,
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.QUOTE_EMAIL]: {
        resourceType: PolicyResourceType.QUOTE,
        previewConfig: {
            canPreview: true,
            skipFields: [],
        },
    },
    [PolicyType.PAYMENT_PLAN_REMINDER]: {
        resourceType: PolicyResourceType.INVOICE, // TODO Confirm this is correct
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.ON_DEMAND_PLACEHOLDER]: {
        resourceType: PolicyResourceType.NOT_APPLICABLE,
        requiresSystem: true,
        hidden: true,
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
};
//# sourceMappingURL=policy.js.map